/* eslint-disable react/no-unescaped-entities */
import { Link, useParams, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, Building2, FileText, Receipt, Calendar, Clock } from "lucide-react";
import PublicHeader from "@/components/layout/PublicHeader";
import PublicContactStrip from "@/components/PublicContactStrip";
import NewsletterSignup from "@/components/NewsletterSignup";
import AnswerFirst from "@/components/seo/AnswerFirst";

// Hyper-local landing pages for the top 12 Canadian rental markets.
// Each maps to its parent province for blog filtering and provincial rules.
export const CITIES = [
  { slug: "toronto",     name: "Toronto",     prov: "Ontario",          provSlug: "ontario",          rent: "$2,650", angle: "LTB backlogs, condo board rules and Toronto's licensing bylaw" },
  { slug: "vancouver",   name: "Vancouver",   prov: "British Columbia", provSlug: "british-columbia", rent: "$2,850", angle: "RTB notices, the empty-homes tax, and Metro Van STR bylaws" },
  { slug: "montreal",    name: "Montréal",    prov: "Quebec",           provSlug: "quebec",           rent: "$1,750", angle: "Le bail F, TAL rent-fixation, and Quebec's lease-renewal rules" },
  { slug: "calgary",     name: "Calgary",     prov: "Alberta",          provSlug: "alberta",          rent: "$1,950", angle: "no rent cap, RTDRS hearings, and Calgary's secondary-suite registration" },
  { slug: "edmonton",    name: "Edmonton",    prov: "Alberta",          provSlug: "alberta",          rent: "$1,500", angle: "no rent cap, RTDRS access, and Edmonton's secondary-suite licence" },
  { slug: "ottawa",      name: "Ottawa",      prov: "Ontario",          provSlug: "ontario",          rent: "$2,200", angle: "Ontario Standard Lease, LTB filings, and Ottawa's STR bylaw" },
  { slug: "hamilton",    name: "Hamilton",    prov: "Ontario",          provSlug: "ontario",          rent: "$1,850", angle: "Hamilton-specific licensing pilot and Ontario rent-cap math" },
  { slug: "mississauga", name: "Mississauga", prov: "Ontario",          provSlug: "ontario",          rent: "$2,550", angle: "Peel Region rental rules and Ontario LTB filings" },
  { slug: "london",      name: "London",      prov: "Ontario",          provSlug: "ontario",          rent: "$1,750", angle: "London ON's STR registry and Ontario notice rules" },
  { slug: "halifax",     name: "Halifax",     prov: "Nova Scotia",      provSlug: "nova-scotia",      rent: "$2,300", angle: "the 5% NS rent cap and HRM's short-term rental registry" },
  { slug: "winnipeg",    name: "Winnipeg",    prov: "Manitoba",         provSlug: "manitoba",         rent: "$1,400", angle: "Manitoba's annual guideline and the Residential Tenancies Branch" },
  { slug: "victoria",    name: "Victoria",    prov: "British Columbia", provSlug: "british-columbia", rent: "$2,400", angle: "BC rent caps and Victoria's strict STR-licensing rules" },
  { slug: "regina",      name: "Regina",      prov: "Saskatchewan",     provSlug: "saskatchewan",     rent: "$1,300", angle: "Saskatchewan's Office of Residential Tenancies and Regina's rental licensing" },
  { slug: "saskatoon",   name: "Saskatoon",   prov: "Saskatchewan",     provSlug: "saskatchewan",     rent: "$1,350", angle: "Saskatchewan ORT hearings and Saskatoon's secondary-suite bylaw" },
];
export const CITY_SLUGS = CITIES.map((c) => c.slug);
export const CITY_INDEX = CITIES.reduce((m, c) => { m[c.slug] = c; return m; }, {});

export default function City() {
  const { city } = useParams();
  const c = CITY_INDEX[city];
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${window.location.origin}/api/blog/posts`);
        const all = r.ok ? await r.json() : [];
        const filtered = (Array.isArray(all) ? all : [])
          .filter((p) => p.category === c?.prov)
          .slice(0, 3);
        if (!cancelled) setPosts(filtered);
      } catch { if (!cancelled) setPosts([]); }
    })();
    return () => { cancelled = true; };
  }, [c]);

  useEffect(() => {
    if (!c) return;
    const url = `https://centralrentals.ca/landlord-software/city/${c.slug}`;
    const title = `Property management software for ${c.name} landlords · Central Rentals`;
    const desc = `Built for ${c.name} landlords. We handle ${c.angle}. Paid plans from CA$35/month · Cancel anytime.`;
    document.title = title;
    const setTag = (sel, attrs) => {
      let el = document.head.querySelector(sel);
      if (!el) { el = document.createElement(sel.startsWith("link") ? "link" : "meta"); document.head.appendChild(el); }
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      return el;
    };
    const tags = [
      setTag('meta[name="description"]', { name: "description", content: desc }),
      setTag('link[rel="canonical"]', { rel: "canonical", href: url }),
      setTag('meta[property="og:title"]', { property: "og:title", content: title }),
      setTag('meta[property="og:description"]', { property: "og:description", content: desc }),
      setTag('meta[property="og:url"]', { property: "og:url", content: url }),
      setTag('meta[name="twitter:title"]', { name: "twitter:title", content: title }),
    ];
    const ld = {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": `Central Rentals — Property Management Software for ${c.name}`,
      "provider": { "@type": "Organization", "name": "Central Rentals Canada", "url": "https://centralrentals.ca/" },
      "areaServed": { "@type": "City", "name": c.name, "addressRegion": c.prov, "addressCountry": "CA" },
      "url": url, "description": desc,
    };
    let script = document.head.querySelector('script[data-city-jsonld="1"]');
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-city-jsonld", "1");
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(ld);
    return () => { tags.forEach((el) => el && el.remove()); if (script) script.remove(); };
  }, [c]);

  if (!c) return <Navigate to="/" replace />;
  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid={`city-${c.slug}`}>
      <PublicHeader light={true}/>
      <section className="max-w-6xl mx-auto px-5 md:px-8 pt-10 pb-6">
        <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-emerald-700 mb-3">
          {c.name}, {c.prov} · Property management software
        </div>
        <h1 className="font-display text-4xl md:text-6xl font-extrabold tracking-tighter leading-[1.02] text-slate-900">
          Built for <span className="text-emerald-700">{c.name}</span> landlords.
        </h1>
        <AnswerFirst>
          Central Rentals is the property management software {c.name} landlords use to collect rent in CAD, generate {c.prov}-compliant notices, and file CRA-ready T776 reports. Median 1-bedroom rent in {c.name} is around {c.rent}/month — every dollar tracked, every notice compliant, from CA$35/mo.
        </AnswerFirst>
        <p className="mt-5 text-lg md:text-xl text-slate-600 max-w-3xl leading-relaxed">
          We handle {c.angle}. Median 1-bedroom rent in {c.name} is around <b>{c.rent}/mo</b> — we make sure every cent is collected, every notice is compliant, and every receipt is CRA-ready.
        </p>
        <div className="mt-7 flex flex-wrap items-center gap-3">
          <Link to="/signup?role=landlord&plan=professional" data-testid={`city-${c.slug}-cta`}
            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold shadow-sm">
            Choose a plan <ArrowRight className="w-4 h-4"/>
          </Link>
          <Link to={`/landlord-software/${c.provSlug}`} className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-slate-200 hover:border-emerald-700 text-slate-800 font-semibold">
            See {c.prov} rules
          </Link>
        </div>
      </section>

      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-14">
          <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tighter text-slate-900 max-w-2xl mb-7">
            What {c.name} landlords use us for.
          </h2>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { icon: FileText, t: `${c.prov} lease + notices`, d: `Province-compliant lease for ${c.prov} with the right notice-form package built in.` },
              { icon: Receipt, t: "Rent tracking", d: `Peer-to-peer rent tracking in CAD, auto-receipts, and CRA-ready T776 / NR4 exports.` },
              { icon: Building2, t: "Local-bylaw checks", d: `${c.name} licensing, STR registry, and condo-board rule cross-references at a glance.` },
            ].map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="p-6 rounded-2xl bg-white border border-slate-200">
                  <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center mb-4"><Icon className="w-5 h-5"/></div>
                  <h3 className="font-display text-lg font-bold text-slate-900">{f.t}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {posts.length > 0 && (
        <section className="max-w-6xl mx-auto px-5 md:px-8 py-14">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-700 mb-3">Field notes for {c.prov}</div>
          <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tighter text-slate-900 mb-7">Latest reads for {c.name} landlords</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {posts.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} className="group block rounded-2xl border border-slate-200 bg-white hover:border-emerald-200 hover:shadow-md transition-all overflow-hidden">
                {p.hero_image && (
                  <div className="aspect-[16/9] bg-slate-100 overflow-hidden">
                    <img src={p.hero_image} alt={p.hero_alt || p.title} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
                  </div>
                )}
                <div className="p-5">
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">{p.category}</div>
                  <h3 className="font-display mt-2 text-base font-bold leading-snug text-slate-900 group-hover:text-emerald-800">{p.title}</h3>
                  <div className="mt-3 flex items-center gap-3 text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3"/>{p.published_at ? new Date(p.published_at).toLocaleDateString("en-CA", {month: "short", day: "numeric", year: "numeric"}) : ""}</span>
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3"/>{p.read_mins} min</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="bg-gradient-to-br from-emerald-50 via-white to-sky-50 border-t border-slate-200">
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-14 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tighter text-slate-900">
            {c.name} landlords run on Central Rentals.
          </h2>
          <p className="mt-3 text-slate-600 max-w-xl mx-auto">Cancel any time. Cancel any time. Cancel any time.</p>
          <Link to="/signup?role=landlord&plan=professional" className="mt-7 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold shadow-sm">
            Choose a plan <ArrowRight className="w-4 h-4"/>
          </Link>
          <div className="mt-10 max-w-md mx-auto text-left">
            <NewsletterSignup source={`city-${c.slug}`} compact />
          </div>
        </div>
      </section>
      <PublicContactStrip testid={`city-contact-${c.slug}`} />
    </div>
  );
}
